import { useState } from 'react';
import axios from 'axios';
import { X, RotateCcw, Eye, CheckCircle2, Pencil } from 'lucide-react';
import DocHtmlEditor from './DocHtmlEditor';

// Revisione della bozza del verbale, una sezione alla volta (backend routes/onboarding.ts). Le
// sezioni toccate qui restano tali anche quando la bozza si riallinea ai dati del wizard, finché
// non si ripristinano. {{revision}} e {{release_date}} si riempiono quando diventa definitivo.

export interface DraftSection { key: string; title: string; body: string; enabled: boolean; edited: boolean; customized?: boolean }
export interface Draft {
  sections: DraftSection[]; staleEdited: string[]; version: number; updatedAt: string; updatedByName: string | null; revision: number; missingFields: number; outdated: boolean;
}
type Change = { key: string; title?: string; body?: string; enabled?: boolean };

export default function OnboardingDraftEditor({ tenantId, draft, onClose, onChanged, onPreview, onFinalize }: {
  tenantId: number; draft: Draft; onClose: () => void; onChanged: (draft: Draft) => void;
  onPreview: () => void; onFinalize: () => void;
}) {
  const [current, setCurrent] = useState(draft);
  const [selected, setSelected] = useState(draft.sections.find(s => s.enabled)?.key ?? draft.sections[0]?.key);
  const [changes, setChanges] = useState<Record<string, Change>>({});
  // Il testo in lavorazione vive nell'editor: rimontarlo solo quando cambia da fuori (ripristino).
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = Object.keys(changes).length > 0;
  const section = current.sections.find(s => s.key === selected);
  const view = (s: DraftSection) => ({ ...s, ...changes[s.key] });

  function change(key: string, patch: Omit<Change, 'key'>) {
    setChanges(c => ({ ...c, [key]: { ...c[key], key, ...patch } }));
  }

  async function save(): Promise<Draft | null> {
    if (!dirty) return current;
    setBusy(true); setError(null);
    try {
      const res = await axios.put(`/api/onboarding/${tenantId}/document/draft`, { version: current.version, changes: Object.values(changes) });
      setCurrent(res.data.data); setChanges({}); onChanged(res.data.data);
      return res.data.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not save the draft');
      return null;
    } finally { setBusy(false); }
  }

  async function reset(key: string) {
    if (!window.confirm('Discard the changes to this section and write it again from the template and the wizard data?')) return;
    if (!(await save())) return;
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`/api/onboarding/${tenantId}/document/draft`, { resetKeys: [key] });
      setCurrent(res.data.data); setChanges({}); setEditorEpoch(e => e + 1); onChanged(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Could not restore the section');
    } finally { setBusy(false); }
  }

  function close() {
    if (dirty && !window.confirm('Close without saving your changes?')) return;
    onClose();
  }

  const merged = section ? view(section) : null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Edit onboarding document draft">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div>
            <h3 className="font-medium text-gray-900 inline-flex items-center gap-2"><Pencil size={15} className="text-primary-600" /> Onboarding document draft (Rev.{current.revision})</h3>
            <p className="text-xs text-gray-500 mt-0.5">Edit the text as it will be printed. Highlighted fields are still to be defined. Revision and date are filled in when the document becomes final.</p>
          </div>
          <button onClick={close} aria-label="Close" className="text-gray-500 hover:text-gray-800"><X size={18} /></button>
        </div>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <nav aria-label="Sections" className="md:w-64 shrink-0 border-b md:border-b-0 md:border-r border-gray-100 overflow-auto max-h-40 md:max-h-none p-2 space-y-0.5">
            {current.sections.map(s => {
              const v = view(s);
              return (
                <div key={s.key} className={`flex items-start gap-2 rounded px-2 py-1.5 text-sm ${s.key === selected ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
                  <input type="checkbox" aria-label={`Include ${v.title}`} className="mt-0.5" checked={v.enabled} onChange={e => change(s.key, { enabled: e.target.checked })} />
                  <button onClick={() => setSelected(s.key)} className={`text-left flex-1 min-w-0 ${v.enabled ? 'text-gray-800' : 'text-gray-400 line-through'}`}>
                    <span className="block truncate" title={v.title}>{v.title}</span>
                    {(s.edited || s.customized || changes[s.key]) && <span className="text-[10px] text-amber-700">{changes[s.key] ? 'unsaved' : current.staleEdited.includes(s.key) ? 'edited, data changed' : 'edited'}</span>}
                  </button>
                </div>
              );
            })}
          </nav>

          <div className="flex-1 min-w-0 p-3 overflow-auto space-y-2">
            {merged && (
              <>
                <div className="flex items-end gap-2">
                  <label className="block text-xs text-gray-500 flex-1">Section title
                    <input className="input-field w-full text-sm" maxLength={120} value={merged.title} onChange={e => change(merged.key, { title: e.target.value })} />
                  </label>
                  {(section!.edited || section!.customized || changes[merged.key]) && (
                    <button onClick={() => reset(merged.key)} disabled={busy} className="btn-secondary inline-flex items-center gap-1 text-sm"><RotateCcw size={13} /> Restore</button>
                  )}
                </div>
                {current.staleEdited.includes(merged.key) && !changes[merged.key]?.body && (
                  <p className="text-xs text-amber-800">The wizard data behind this section changed after it was edited by hand, and this text does not include the change. Check it, or restore the section.</p>
                )}
                <DocHtmlEditor key={`${merged.key}|${editorEpoch}`} html={merged.body} onChange={body => change(merged.key, { body })} />
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-gray-100">
          {error && <p className="text-sm text-red-700 mr-auto" role="alert">{error}</p>}
          {!error && <p className="text-xs text-gray-500 mr-auto">{dirty ? 'Unsaved changes' : `Saved ${new Date(current.updatedAt).toLocaleString('en-GB')}${current.updatedByName ? ` · ${current.updatedByName}` : ''}`}</p>}
          <button onClick={save} disabled={busy || !dirty} className="btn-secondary">{busy ? 'Saving…' : 'Save draft'}</button>
          <button onClick={async () => { if (await save()) onPreview(); }} disabled={busy} className="btn-secondary inline-flex items-center gap-1"><Eye size={13} /> Preview</button>
          <button onClick={async () => { if (await save()) onFinalize(); }} disabled={busy} className="btn-primary inline-flex items-center gap-1"><CheckCircle2 size={13} /> Make final</button>
        </div>
      </div>
    </div>
  );
}
